import React from 'react';

/**
 * Error Boundary to catch React rendering errors and prevent app crash
 * Specifically handles chunk loading errors from code splitting
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a chunk loading error
    const isChunkError =
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk') ||
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.name === 'ChunkLoadError';

    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ error, errorInfo });

    // For chunk errors, log additional info
    if (this.state.isChunkError) {
      console.warn('[AppErrorBoundary] Chunk load error detected. User may need to refresh.');
    }
  }

  handleReload = () => {
    // Clear service worker cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach(name => {
          caches.delete(name);
        });
      }).then(() => {
        window.location.reload(true);
      });
    } else {
      window.location.reload(true);
    }
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, isChunkError: false });
  };

  render() {
    if (this.state.hasError) {
      // Special UI for chunk loading errors
      if (this.state.isChunkError) {
        return (
          <div style={{
            padding: 32,
            textAlign: 'center',
            maxWidth: 400,
            margin: '100px auto'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔄</div>
            <h2 style={{ color: '#1a365d', marginBottom: 16 }}>Update Available</h2>
            <p style={{ color: '#4a5568', marginBottom: 24, lineHeight: 1.6 }}>
              A new version of the application is available. Please refresh the page to get the latest updates.
            </p>
            <button
              onClick={this.handleReload}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '12px 32px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 500
              }}
            >
              Refresh Page
            </button>
          </div>
        );
      }

      // Standard error UI for other errors
      return (
        <div style={{
          padding: 16,
          background: '#ffebee',
          border: '1px solid #f44336',
          borderRadius: 8,
          margin: 16
        }}>
          <h2 style={{ color: '#c62828', marginBottom: 16 }}>Something went wrong</h2>
          <details>
            <summary style={{ cursor: 'pointer', marginBottom: 8 }}>Error Details</summary>
            <pre style={{
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 4,
              fontSize: 12,
              overflow: 'auto'
            }}>
              {this.state.error?.message}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
          </details>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button
              onClick={this.handleRetry}
              style={{
                background: '#2196f3',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
            <button
              onClick={this.handleReload}
              style={{
                background: '#666',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
